import { createTheme } from '@mui/material/styles';

export const getAppTheme = (mode = 'light', direction = 'rtl') => {
  const isDark = mode === 'dark';
  const isRtl = direction === 'rtl';

  const arabicFont = "'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const englishFont = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

  return createTheme({
    direction: direction,
    palette: {
      mode,
      primary: {
        main: '#EF6C00', // FastLine Vibrant Orange
        light: '#FF8A00',
        dark: '#D85A00',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#1E255E', // FastLine Deep Royal Navy
        light: '#28327A',
        dark: '#141A45',
        contrastText: '#FFFFFF',
      },
      background: {
        default: isDark ? '#0F172A' : '#F8FAFC',
        paper: isDark ? '#1E293B' : '#FFFFFF',
      },
      text: {
        primary: isDark ? '#F8FAFC' : '#1E293B',
        secondary: isDark ? '#94A3B8' : '#64748B',
      },
      success: {
        main: '#10B981',
        light: '#ECFDF5',
        dark: '#047857',
      },
      warning: {
        main: '#F59E0B',
        light: '#FFFBEB',
        dark: '#B45309',
      },
      error: {
        main: '#EF4444',
        light: '#FEF2F2',
        dark: '#B91C1C',
      },
      info: {
        main: '#3B82F6',
        light: '#EFF6FF',
        dark: '#1D4ED8',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
    },
    typography: {
      fontFamily: isRtl ? arabicFont : englishFont,
      fontSize: 13,
      h1: { fontWeight: 800, fontSize: '1.8rem', letterSpacing: -0.5 },
      h2: { fontWeight: 700, fontSize: '1.5rem' },
      h3: { fontWeight: 700, fontSize: '1.3rem' },
      h4: { fontWeight: 700, fontSize: '1.15rem' },
      h5: { fontWeight: 700, fontSize: '1rem' },
      h6: { fontWeight: 700, fontSize: '0.92rem' },
      subtitle1: { fontWeight: 600, fontSize: '0.88rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.82rem' },
      body1: { fontWeight: 400, fontSize: '0.85rem' },
      body2: { fontWeight: 400, fontSize: '0.78rem' },
      caption: { fontWeight: 500, fontSize: '0.72rem' },
      button: {
        fontWeight: 600,
        fontSize: '0.82rem',
        textTransform: 'none',
        borderRadius: '8px',
      },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '6px 14px',
            boxShadow: 'none',
            fontSize: '0.8rem',
            fontFamily: isRtl ? arabicFont : englishFont,
            '&:hover': {
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: 'none',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #E2E8F0',
            boxShadow: isDark
              ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)'
              : '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            direction: direction,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '10px 14px',
            fontSize: '0.8rem',
            textAlign: isRtl ? 'right' : 'left',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#F1F5F9',
          },
          head: {
            fontWeight: 700,
            fontSize: '0.78rem',
            color: isDark ? '#94A3B8' : '#64748B',
            backgroundColor: isDark ? '#1E293B' : '#F8FAFC',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: 6,
            height: 24,
            fontSize: '0.72rem',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            direction: direction,
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              fontSize: '0.84rem',
              direction: direction,
            },
            '& .MuiOutlinedInput-input': {
              textAlign: isRtl ? 'right' : 'left',
              direction: direction,
            },
            '& .MuiInputLabel-root': {
              textAlign: isRtl ? 'right' : 'left',
              direction: direction,
              transformOrigin: isRtl ? 'top right' : 'top left',
            },
            '& .MuiInputLabel-root.MuiInputLabel-shrink': {
              backgroundColor: isDark ? '#1E293B' : '#FFFFFF',
              padding: '0 6px',
              borderRadius: '4px',
              zIndex: 2,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              textAlign: isRtl ? 'right' : 'left',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            direction: direction,
          },
          input: {
            textAlign: isRtl ? 'right' : 'left',
            direction: direction,
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            direction: direction,
          },
          positionStart: {
            marginRight: isRtl ? '0px !important' : '10px !important',
            marginLeft: isRtl ? '10px !important' : '0px !important',
          },
          positionEnd: {
            marginLeft: isRtl ? '0px !important' : '10px !important',
            marginRight: isRtl ? '10px !important' : '0px !important',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            direction: direction,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            textAlign: isRtl ? 'right' : 'left',
            direction: direction,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            justifyContent: isRtl ? 'flex-start' : 'flex-end',
            padding: '12px 16px',
            gap: '8px',
            direction: direction,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            direction: direction,
          },
          flexContainer: {
            direction: direction,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: isRtl ? arabicFont : englishFont,
            fontWeight: 700,
            direction: direction,
          },
        },
      },
    },
  });
};
